import { z } from "zod";
export const taskKeySchema = z.enum([
  "collect_reddit_posts",
  "collect_reddit_comments",
  "collect_stargazers",
  "collect_issues",
  "collect_activity",
  "backfill_archived_stars",
  "enrich_profiles",
  "enrich_email",
  "prepare_records",
  "deliver_records",
]);
export type TaskKey = z.infer<typeof taskKeySchema>;
export const taskDefinitionSchema = z.object({
  key: taskKeySchema,
  name: z.string(),
});
/**
 * Progress of one task, recorded by the workflow itself in its Temporal memo.
 * Temporal is the only place run state lives; the database never stores it.
 */
export const runTaskSchema = taskDefinitionSchema.extend({
  status: z.enum(["queued", "running", "completed", "partial", "failed", "cancelled"]),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
});
export type RunTask = z.infer<typeof runTaskSchema>;
/** The memo every collection workflow keeps current under `progress`. */
export const runProgressSchema = z.object({ tasks: z.array(runTaskSchema) });
export type RunProgress = z.infer<typeof runProgressSchema>;
/**
 * Tasks, by key.
 *
 * `name` is not merely a label: it is the string a workflow passes to a
 * provider module, which switches on it. The UI and the runtime therefore have
 * to read it from here rather than each spelling it out, because when they
 * drift the module falls through to its default and collects nothing while
 * every screen reports success. That is exactly what happened to community
 * activity, which was named two different ways and never ran.
 */
export const taskDefinitions = {
  collect_reddit_posts: {
    key: "collect_reddit_posts",
    name: "Collect Reddit posts",
  },
  collect_reddit_comments: {
    key: "collect_reddit_comments",
    name: "Collect Reddit comments",
  },
  collect_stargazers: { key: "collect_stargazers", name: "Collect stargazers" },
  collect_issues: { key: "collect_issues", name: "Collect issues" },
  collect_activity: {
    key: "collect_activity",
    name: "Collect community activity",
  },
  backfill_archived_stars: {
    key: "backfill_archived_stars",
    name: "Backfill archived stars",
  },
  enrich_email: { key: "enrich_email", name: "Enrich email from commit patches" },
  enrich_profiles: { key: "enrich_profiles", name: "Enrich profiles" },
  prepare_records: { key: "prepare_records", name: "Prepare records" },
  deliver_records: { key: "deliver_records", name: "Deliver records" },
} as const;

/** The task names a workflow definition is built from. */
export const TASK_NAMES = {
  collectRedditPosts: taskDefinitions.collect_reddit_posts.name,
  collectRedditComments: taskDefinitions.collect_reddit_comments.name,
  collectStargazers: taskDefinitions.collect_stargazers.name,
  collectIssues: taskDefinitions.collect_issues.name,
  collectActivity: taskDefinitions.collect_activity.name,
  backfillArchivedStars: taskDefinitions.backfill_archived_stars.name,
  enrichProfiles: taskDefinitions.enrich_profiles.name,
  prepareRecords: taskDefinitions.prepare_records.name,
  deliverRecords: taskDefinitions.deliver_records.name,
} as const;
